import { Provider } from 'mobx-react';
import * as React from 'react';
import { Switch, useLocation, useParams, useRouteMatch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { ILocationState, IMultiCampaignSchedulerRequest, IMultiCampaignSchedulerResult } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAdminAccountQuery } from '../../../../queries';
import { ImpersonationContextViewModel } from '../../../../viewmodels/AdminViewModels';
import { CampaignCalendarSuggestionsProvider } from '../../../../web/components/campaigns/CampaignCalendar/context';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { PrivateRoute } from '../../../../web/components/PrivateRoute';
import { ScheduleSuccess } from '../../../../web/containers/blogs/ScheduleSuccess';
import { MultiBlogScheduler } from '../../../../web/containers/MultiCampaignScheduler/components/blogs/MultiBlogScheduler';

/**
 * Admin wrapper for the blog multi-scheduler that fetches the account from URL params
 * and provides impersonation context. This mirrors AccountMultiEmailScheduler's approach
 * to avoid relying on location state for impersonation (which gets lost in the fullscreen
 * modal's memory router).
 */
export function AccountMultiBlogScheduler() {
	const params = useParams<{ accountId: string }>();
	const userSession = useUserSession();
	const { url } = useRouteMatch();
	const history = useHistory();
	const location = useLocation<ILocationState<any, IMultiCampaignSchedulerRequest>>();
	const request = location.state?.model;
	const isSequenceStep = request?.isSequenceStep;
	// Keep the ref in sync with the latest `onFinish` so replays of the memory router (which can
	// re-render this component with updated location state) don't invoke a stale callback.
	const onFinishRef = React.useRef(request?.onFinish);
	onFinishRef.current = request?.onFinish;

	const [impersonationContext, setImpersonationContext] = React.useState<ImpersonationContextViewModel | null>(null);

	const adminAccountQuery = useAdminAccountQuery({
		accountId: params.accountId,
	});

	React.useEffect(() => {
		if (adminAccountQuery.data) {
			setImpersonationContext(
				new ImpersonationContextViewModel({
					account: adminAccountQuery.data,
				})
			);
		}
	}, [adminAccountQuery.data]);

	const onScheduleSuccess = React.useCallback(
		(e: CustomEvent<IMultiCampaignSchedulerResult>) => {
			if (isSequenceStep) {
				onFinishRef.current?.();
				return;
			}

			e.preventDefault();
			history.push({
				pathname: `${url}/success`,
				state: location.state,
			});

			onFinishRef.current?.();
		},
		[history, isSequenceStep, location.state, url]
	);

	if (!impersonationContext) {
		return <LoadingSpinner />;
	}

	return (
		<Provider impersonationContext={impersonationContext}>
			<CampaignCalendarSuggestionsProvider
				initialSelectedSuggestions={request?.suggestions}
				supportsBulkScheduleSuggestions
				onSuccess={onScheduleSuccess}
				skipClearOnSuccess={isSequenceStep}
			>
				<Switch>
					<PrivateRoute path={`${url}/success`} userSession={userSession}>
						<ScheduleSuccess multiple />
					</PrivateRoute>
					<PrivateRoute path={url} userSession={userSession}>
						<MultiBlogScheduler />
					</PrivateRoute>
				</Switch>
			</CampaignCalendarSuggestionsProvider>
		</Provider>
	);
}
